/**
 * KpiPanel — Dynamic Output mock for "Show me ops dashboard KPIs"
 * (Rev 25 Phase 2 Loop I). Static content per Home.html artboard;
 * Phase 4 wires Anthropic tool-use to generate the values from the
 * conversation context.
 */

type Kpi = {
  label: string;
  value: string;
  delta: string;
  trend: "up" | "down" | "warn";
  spark: string;
};

const KPIS: Kpi[] = [
  {
    label: "Jobs dispatched",
    value: "1,284",
    delta: "↑ 8.4% vs. prev 30d",
    trend: "up",
    spark: "0,22 10,18 20,20 30,14 40,16 50,10 60,12 70,7 80,9 90,4 100,6",
  },
  {
    label: "On-time arrival",
    value: "96.4%",
    delta: "↑ 1.1pp",
    trend: "up",
    spark: "0,12 12,14 24,10 36,11 48,8 60,9 72,7 84,8 100,5",
  },
  {
    label: "Avg. response",
    value: "22m",
    delta: "↓ -3m",
    trend: "warn",
    spark: "0,8 10,10 20,12 30,11 40,14 50,12 60,16 70,15 80,18 90,17 100,20",
  },
];

const TREND_COLORS: Record<Kpi["trend"], string> = {
  up: "var(--nx-success)",
  down: "var(--nx-danger)",
  warn: "var(--nx-warn)",
};

const SPARK_COLORS: Record<Kpi["trend"], string> = {
  up: "#6B4FE6",
  down: "#F43F5E",
  warn: "#F59E0B",
};

export function KpiPanel() {
  return (
    <div className="grid gap-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {KPIS.map((k) => (
          <div
            key={k.label}
            className="rounded-nx2 border border-nx-line bg-nx-surface/60 p-3.5"
          >
            <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-nx-text-faint">
              {k.label}
            </div>
            <div className="mt-2 font-display text-3xl font-semibold leading-none tracking-tight text-nx-text">
              {k.value}
            </div>
            <div
              className="mt-1.5 text-[11.5px]"
              style={{ color: TREND_COLORS[k.trend] }}
            >
              {k.delta}
            </div>
            <svg
              viewBox="0 0 100 30"
              preserveAspectRatio="none"
              className="mt-2 h-7 w-full"
              aria-hidden
            >
              <polyline
                fill="none"
                stroke={SPARK_COLORS[k.trend]}
                strokeWidth="1.5"
                points={k.spark}
              />
            </svg>
          </div>
        ))}
      </div>

      <div className="rounded-nx2 border border-nx-line bg-nx-surface/60 p-3.5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h4 className="font-display text-sm font-semibold text-nx-text">
            Dispatch volume · last 14 days
          </h4>
          <div className="flex items-center gap-3 font-mono text-[10.5px] text-nx-text-faint">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-1.5 w-2.5" style={{ background: "#6B4FE6" }} />
              Completed
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-1.5 w-2.5" style={{ background: "#22D3EE" }} />
              Scheduled
            </span>
          </div>
        </div>
        <svg
          viewBox="0 0 600 140"
          preserveAspectRatio="none"
          className="mt-3 h-32 w-full"
          aria-hidden
        >
          <defs>
            <linearGradient id="kpi-grad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" stopColor="#6B4FE6" stopOpacity="0.5" />
              <stop offset="1" stopColor="#6B4FE6" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d="M0,110 L0,80 C40,75 60,60 100,55 C140,50 180,72 220,68 C260,64 300,38 340,42 C380,46 420,30 460,28 C500,26 540,18 600,22 L600,110 Z"
            fill="url(#kpi-grad)"
          />
          <path
            d="M0,80 C40,75 60,60 100,55 C140,50 180,72 220,68 C260,64 300,38 340,42 C380,46 420,30 460,28 C500,26 540,18 600,22"
            fill="none"
            stroke="#6B4FE6"
            strokeWidth="2"
          />
          <path
            d="M0,95 C40,92 80,98 140,90 C200,80 260,88 320,82 C380,76 440,82 500,72 C560,64 580,58 600,55"
            fill="none"
            stroke="#22D3EE"
            strokeWidth="1.5"
            strokeDasharray="3 3"
          />
        </svg>
      </div>
    </div>
  );
}

export default KpiPanel;
